/**
 * Líneas, retención y notas de un documento, y la tarjeta de totales —
 * compartidos por el presupuesto y el borrador de factura. Las líneas de
 * factura son idénticas a las de presupuesto (`TDS-CORE` Parte 3 §1): una sola
 * pantalla para las dos, no dos copias que puedan divergir.
 *
 * **No calcula importes**: los totales llegan de la API (`URS §129`).
 */
import type { CatalogoIva, LineaPresupuesto, Producto, TotalesDocumento, TratamientoIva } from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible } from "./formato";
import { AreaTexto, Aviso, Boton, Buscador, Campo, Casilla, Selector, Tarjeta } from "./primitivas";

export const LINEA_VACIA: LineaPresupuesto = {
  productId: null,
  description: "",
  quantity: "1",
  unitPrice: "",
  discount: { kind: "NONE" },
  vatTreatment: "SUJETA_NO_EXENTA",
  vatRate: "21,00",
  vatCause: null,
  withholdingApplies: false,
};

const TRATAMIENTOS: ReadonlyArray<{ readonly valor: TratamientoIva; readonly texto: string }> = (
  ["SUJETA_NO_EXENTA", "SUJETA_EXENTA", "NO_SUJETA"] as const
).map((v) => ({ valor: v, texto: t.producto.tratamientos[v] }));

const DESCUENTOS = (["NONE", "AMOUNT", "PERCENT"] as const).map((v) => ({ valor: v, texto: t.presupuesto.descuentos[v] }));

const RETENCIONES = [
  { valor: "NINGUNA" as const, texto: t.presupuesto.retenciones.NINGUNA },
  { valor: "15,00" as const, texto: t.presupuesto.retenciones["1500"] },
  { valor: "7,00" as const, texto: t.presupuesto.retenciones["700"] },
];

const MOTIVOS = (["INICIO_ACTIVIDAD_COMUNICADO", "EPIGRAFE_IAE_ART_95_1"] as const).map((v) => ({
  valor: v,
  texto: t.presupuesto.motivos[v],
}));

/** Texto escrito en el buscador de producto de cada línea. */
type Busquedas = Readonly<Record<number, string>>;

/** Lo que tienen en común presupuesto y factura. */
export type DatosLineas = {
  readonly notes: string | null;
  readonly withholdingRate: string | null;
  readonly withholdingReason: string | null;
  readonly lines: readonly LineaPresupuesto[];
};

export function EditorLineas<D extends DatosLineas>({
  prefijo,
  datos,
  setDatos,
  catalogo,
  productos,
  busquedaProducto,
  setBusquedaProducto,
  totals,
  sugerencia,
  pendienteRetencion,
  errorDe,
  errorLinea,
}: {
  /** Prefijo de los ids de los campos ("pre", "fac"). */
  prefijo: string;
  datos: D;
  setDatos: (cambio: (d: D) => D) => void;
  catalogo: CatalogoIva | null;
  productos: readonly Producto[];
  busquedaProducto: Busquedas;
  setBusquedaProducto: (cambio: (b: Busquedas) => Busquedas) => void;
  totals: TotalesDocumento | null;
  /** Retención sugerida por la API ("15,00") o null. Nunca se aplica sola. */
  sugerencia: string | null;
  pendienteRetencion: boolean;
  errorDe: (campo: string) => string | null;
  errorLinea: (i: number, campo: string) => string | null;
}) {
  function cambiarLinea(i: number, cambio: Partial<LineaPresupuesto>): void {
    setDatos((d) => ({ ...d, lines: d.lines.map((l, j) => (i === j ? { ...l, ...cambio } : l)) }));
  }

  /** Elegir un producto COPIA sus datos en la línea; después se pueden editar (R-P2). */
  function elegirProducto(i: number, productId: string | null): void {
    if (productId === null) return cambiarLinea(i, { productId: null });
    const p = productos.find((x) => x.id === productId);
    if (p === undefined) return;
    cambiarLinea(i, {
      productId: p.id,
      description: p.description,
      unitPrice: p.unitPrice,
      vatTreatment: p.vatTreatment,
      vatRate: p.vatRate,
      vatCause: p.vatCause,
      withholdingApplies: p.withholdingDefault,
    });
  }

  const tipos = (catalogo?.vatRates ?? []).map((v) => ({ valor: v, texto: t.producto.tipoOpcion(v) }));
  const opcionesProducto = productos.map((p) => ({ id: p.id, texto: `${p.code} · ${p.description}` }));

  return (
    <>
      <h3>{t.presupuesto.lineas}</h3>
      {errorDe("lines") !== null ? <Aviso tono="error">{errorDe("lines")}</Aviso> : null}
      {datos.lines.map((l, i) => (
        <fieldset key={i} className="linea-documento">
          <legend>{`${i + 1}`}</legend>
          <div className="fila-campos">
            <Buscador
              id={`${prefijo}-producto-${i}`}
              etiqueta={t.presupuesto.producto}
              texto={busquedaProducto[i] ?? ""}
              opciones={opcionesProducto}
              marcador={t.presupuesto.buscaProducto}
              ayuda={t.presupuesto.ayudaProducto}
              onTexto={(v) => setBusquedaProducto((b) => ({ ...b, [i]: v }))}
              onElegir={(pid) => elegirProducto(i, pid)}
              error={errorLinea(i, "productId")}
            />
            <Campo
              id={`${prefijo}-descripcion-${i}`}
              etiqueta={t.presupuesto.descripcion}
              valor={l.description}
              onChange={(v) => cambiarLinea(i, { description: v })}
              autoComplete="off"
              error={errorLinea(i, "description")}
            />
          </div>
          <div className="fila-campos">
            <Campo
              id={`${prefijo}-cantidad-${i}`}
              etiqueta={t.presupuesto.cantidad}
              valor={l.quantity}
              onChange={(v) => cambiarLinea(i, { quantity: v })}
              autoComplete="off"
              error={errorLinea(i, "quantity")}
            />
            <Campo
              id={`${prefijo}-precio-${i}`}
              etiqueta={t.presupuesto.precio}
              valor={l.unitPrice}
              onChange={(v) => cambiarLinea(i, { unitPrice: v })}
              autoComplete="off"
              error={errorLinea(i, "unitPrice")}
            />
            <Selector
              id={`${prefijo}-descuento-${i}`}
              etiqueta={t.presupuesto.descuento}
              valor={l.discount.kind}
              opciones={DESCUENTOS}
              onChange={(v) =>
                cambiarLinea(i, { discount: v === "NONE" ? { kind: "NONE" } : { kind: v, value: l.discount.kind === "NONE" ? "" : l.discount.value } })
              }
              error={errorLinea(i, "discount")}
            />
            {l.discount.kind !== "NONE" ? (
              <Campo
                id={`${prefijo}-descuento-valor-${i}`}
                etiqueta={t.presupuesto.valorDescuento}
                valor={l.discount.value}
                onChange={(v) => cambiarLinea(i, { discount: { kind: l.discount.kind as "AMOUNT" | "PERCENT", value: v } })}
                autoComplete="off"
                error={errorLinea(i, "discount")}
              />
            ) : null}
          </div>
          <div className="fila-campos">
            <Selector
              id={`${prefijo}-tratamiento-${i}`}
              etiqueta={t.presupuesto.iva}
              valor={l.vatTreatment}
              opciones={TRATAMIENTOS}
              onChange={(v) =>
                cambiarLinea(i, {
                  vatTreatment: v,
                  vatRate: v === "SUJETA_NO_EXENTA" ? (l.vatRate ?? catalogo?.vatRates[0] ?? null) : null,
                  vatCause: null,
                })
              }
              error={errorLinea(i, "vatTreatment")}
            />
            {l.vatTreatment === "SUJETA_NO_EXENTA" ? (
              <Selector
                id={`${prefijo}-tipo-${i}`}
                etiqueta={t.presupuesto.tipoIva}
                valor={l.vatRate ?? ""}
                opciones={tipos}
                onChange={(v) => cambiarLinea(i, { vatRate: v })}
                error={errorLinea(i, "vatRate")}
              />
            ) : (
              <Selector
                id={`${prefijo}-causa-${i}`}
                etiqueta={t.presupuesto.causaIva}
                valor={l.vatCause ?? ""}
                opciones={[
                  { valor: "", texto: "—" },
                  ...((l.vatTreatment === "SUJETA_EXENTA" ? catalogo?.exemptionCauses : catalogo?.nonSubjectCauses) ?? []).map((c) => ({
                    valor: c,
                    texto: c,
                  })),
                ]}
                onChange={(v) => cambiarLinea(i, { vatCause: v === "" ? null : v })}
                error={errorLinea(i, "vatCause")}
              />
            )}
          </div>
          <Casilla
            id={`${prefijo}-retiene-${i}`}
            etiqueta={t.presupuesto.lineaRetiene}
            marcada={l.withholdingApplies}
            onChange={(v) => cambiarLinea(i, { withholdingApplies: v })}
            error={errorLinea(i, "withholdingApplies")}
          />
          {totals !== null && totals.lines[i] !== undefined ? (
            <p className="ayuda-neutra">
              {totals.lines[i].discount === "0,00"
                ? t.presupuesto.lineaImporte(importeLegible(totals.lines[i].base))
                : t.presupuesto.lineaConDescuento(
                    importeLegible(totals.lines[i].gross),
                    importeLegible(totals.lines[i].discount),
                    importeLegible(totals.lines[i].base),
                  )}
            </p>
          ) : null}
          {datos.lines.length > 1 ? (
            <Boton variante="secundario" onClick={() => setDatos((d) => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }))}>
              {t.presupuesto.quitarLinea}
            </Boton>
          ) : null}
        </fieldset>
      ))}
      <Boton variante="secundario" onClick={() => setDatos((d) => ({ ...d, lines: [...d.lines, LINEA_VACIA] }))}>
        {t.presupuesto.anadirLinea}
      </Boton>

      <h3>{t.presupuesto.retencionTitulo}</h3>
      {sugerencia !== null ? (
        <>
          <Aviso tono="info">{t.presupuesto.sugerencia}</Aviso>
          {datos.withholdingRate === null ? (
            <Boton variante="secundario" onClick={() => setDatos((d) => ({ ...d, withholdingRate: sugerencia }))}>
              {t.presupuesto.aplicarSugerencia}
            </Boton>
          ) : null}
        </>
      ) : null}
      {pendienteRetencion ? <Aviso tono="aviso">{t.presupuesto.pendienteRetencion}</Aviso> : null}
      <div className="fila-campos">
        <Selector
          id={`${prefijo}-retencion`}
          etiqueta={t.presupuesto.retencion}
          valor={datos.withholdingRate ?? "NINGUNA"}
          opciones={RETENCIONES}
          onChange={(v) =>
            setDatos((d) => ({
              ...d,
              withholdingRate: v === "NINGUNA" ? null : v,
              withholdingReason: v === "7,00" ? (d.withholdingReason ?? "INICIO_ACTIVIDAD_COMUNICADO") : null,
            }))
          }
          error={errorDe("withholdingRate")}
        />
        {datos.withholdingRate === "7,00" ? (
          <Selector
            id={`${prefijo}-motivo`}
            etiqueta={t.presupuesto.motivoRetencion}
            valor={datos.withholdingReason ?? "INICIO_ACTIVIDAD_COMUNICADO"}
            opciones={MOTIVOS}
            onChange={(v) => setDatos((d) => ({ ...d, withholdingReason: v }))}
            error={errorDe("withholdingReason")}
          />
        ) : null}
      </div>

      <AreaTexto
        id={`${prefijo}-notas`}
        etiqueta={t.presupuesto.notes}
        valor={datos.notes ?? ""}
        onChange={(v) => setDatos((d) => ({ ...d, notes: v }))}
        ayuda={t.presupuesto.ayudaNotas}
        filas={6}
        error={errorDe("notes")}
      />
    </>
  );
}

/** Totales, retención y desglose de IVA tal como los calcula la API. */
export function TarjetaTotales({ totals }: { totals: TotalesDocumento }) {
  const suplidos = totals.disbursementsTotal ?? null;
  const anticipos = totals.advanceDeductionsTotal ?? null;
  return (
    <Tarjeta titulo={t.presupuesto.totalesTitulo}>
      {/*
        OD-0029 D-06: el TOTAL de la factura y lo que el cliente PAGA son dos
        importes distintos, y el segundo es el que le interesa a quien cobra.
        Por eso la cifra grande es "el cliente paga" CUANDO hay retención, y el
        total de la factura cuando no la hay: con las dos en negrita del mismo
        tamaño, la que se leía primero era la que no se ingresa.

        Los importes siguen llegando calculados de la API: aquí sólo se colocan.
      */}
      <dl className="totales">
        {/* OD-0053 D-03: en la final, la base ya es la neta. Lo que se descontó va antes, para que no parezca que falta. */}
        {anticipos !== null ? (
          <div className="totales-fila">
            <dt>{t.anticipos.totalEtiqueta}</dt>
            <dd>−{importeLegible(anticipos)} €</dd>
          </div>
        ) : null}
        <div className="totales-fila">
          <dt>{t.presupuesto.baseTotal}</dt>
          <dd>{importeLegible(totals.baseTotal)} €</dd>
        </div>
        <div className="totales-fila">
          <dt>{t.presupuesto.ivaTotal}</dt>
          <dd>{importeLegible(totals.vatTotal)} €</dd>
        </div>
        <div className={`totales-fila totales-suma${totals.withholdingRate === null ? " totales-principal" : ""}`}>
          <dt>{t.presupuesto.totalFactura}</dt>
          <dd>{importeLegible(totals.invoiceTotal)} €</dd>
        </div>
        {totals.withholdingRate !== null ? (
          <div className="totales-fila">
            <dt>{t.presupuesto.retencionImporte(totals.withholdingRate)}</dt>
            <dd>−{importeLegible(totals.withholdingAmount)} €</dd>
          </div>
        ) : null}
        {/* OD-0052 D-08: los suplidos van DESPUÉS del total de la factura. */}
        {suplidos !== null ? (
          <div className="totales-fila">
            <dt>{t.suplidos.totalEtiqueta}</dt>
            <dd>{importeLegible(suplidos)} €</dd>
          </div>
        ) : null}
        {totals.withholdingRate !== null || suplidos !== null ? (
          <div className="totales-fila totales-suma totales-principal">
            <dt>{t.presupuesto.clientePaga}</dt>
            <dd>{importeLegible(totals.amountToCollect)} €</dd>
          </div>
        ) : null}
      </dl>
      <p className="ayuda-neutra">
        {totals.withholdingRate === null
          ? t.presupuesto.explicacionTotales
          : t.presupuesto.ayudaClientePaga(importeLegible(totals.vatTotal), importeLegible(totals.withholdingAmount))}
        {suplidos !== null ? ` ${t.suplidos.ayudaTotales(importeLegible(suplidos))}` : null}
        {anticipos !== null ? ` ${t.anticipos.ayudaTotales(importeLegible(anticipos))}` : null}
      </p>
      {/* El desglose por tipo es información del documento, no analítica: sigue
          aquí, pero plegado, porque lo que se consulta a diario son los totales. */}
      <details className="desglose">
        <summary>{t.presupuesto.desgloseVer}</summary>
        <ul className="lista-totales">
          {totals.breakdown.map((d) => (
            <li key={`${d.treatment}-${d.rate ?? d.cause ?? ""}`}>
              {t.presupuesto.desgloseFila(
                d.treatment === "SUJETA_NO_EXENTA"
                  ? t.productos.iva(d.rate ?? "")
                  : d.treatment === "SUJETA_EXENTA"
                    ? t.productos.exenta(d.cause ?? "")
                    : t.productos.noSujeta(d.cause ?? ""),
                importeLegible(d.base),
                d.vatAmount === null ? null : importeLegible(d.vatAmount),
              )}
            </li>
          ))}
        </ul>
      </details>
    </Tarjeta>
  );
}
